import { useState, useEffect, useCallback, useRef, MutableRefObject } from 'react';

export type MediaPlayer = {
  initialize: (
    urls: string[],
    type?:
      | 'aac'
      | 'mp4'
      | 'f4a'
      | 'f4v'
      | 'hls'
      | 'm3u'
      | 'm4v'
      | 'mov'
      | 'mp3'
      | 'mpeg'
      | 'oga'
      | 'ogg'
      | 'ogv'
      | 'vorbis'
      | 'webm',
  ) => void;
  status: 'initializing' | 'playing' | 'paused' | 'stopped' | 'idle';
  urlLoaded: string | undefined;
  play: () => void;
  pause: () => void;
  stop: () => void;
  mute: () => void;
  unmute: () => void;
};

export default function useMediaPlayer(
  errorHandler: (err: unknown) => void,
): [MutableRefObject<HTMLDivElement | null>, MediaPlayer] {
  const playerElementRef = useRef<HTMLDivElement>(null);
  const playerInstanceRef = useRef<jwplayer.JWPlayer | null>(null);
  const [status, setStatus] = useState<MediaPlayer['status']>('initializing');
  const [urlLoaded, setUrlLoaded] = useState<MediaPlayer['urlLoaded']>();

  const handlePlayerInstanceError = useCallback((): void => {
    const error = new Error('no player instance');
    if (errorHandler) errorHandler(error);
    else throw error;
  }, [errorHandler]);

  const handleStop = useCallback(() => {
    setStatus('stopped');
    setUrlLoaded(undefined);
  }, []);

  const initialize: MediaPlayer['initialize'] = useCallback(
    (urls, type) => {
      const { current: divElement } = playerElementRef;
      if (!divElement) return;

      const playlist = urls.filter(x => x).map(url => ({ file: url, type }));

      if (playlist.length) {
        if (!playerInstanceRef.current) {
          const player = window.jwplayer(divElement).setup({
            playlist: playlist,
            autostart: false,
            mute: false,
          });

          player.on('ready', () => setStatus('idle'));
          player.on('play', () => setStatus('playing'));
          player.on('playlistItem', (args: any) => setUrlLoaded(args.item.file));
          player.on('pause', () => setStatus('paused'));
          player.on('playlistComplete', handleStop);
          player.on('error', (err: jwplayer.Error) => errorHandler(err));
          player.on('setupError', (err: jwplayer.Error) => errorHandler(err));

          playerInstanceRef.current = player;
        } else {
          playerInstanceRef.current.load(playlist as any);
        }
      }
    },
    [errorHandler, handleStop],
  );

  useEffect(() => {
    return (): void => {
      const { current: player } = playerInstanceRef;
      if (player) player.remove();
    };
  }, []);

  const play = useCallback(() => {
    const { current: playerInstance } = playerInstanceRef;
    if (!playerInstance) {
      handlePlayerInstanceError();
      return;
    }

    if (status === 'paused') playerInstance.play();
    else playerInstance.playlistItem(0);
  }, [status, handlePlayerInstanceError]);

  const pause = useCallback(() => {
    const { current: playerInstance } = playerInstanceRef;
    if (!playerInstance) {
      handlePlayerInstanceError();
      return;
    }

    playerInstance.pause();
  }, [handlePlayerInstanceError]);

  const stop = useCallback(() => {
    const { current: playerInstance } = playerInstanceRef;
    if (!playerInstance) {
      handlePlayerInstanceError();
      return;
    }

    playerInstance.stop();
    handleStop();
  }, [handlePlayerInstanceError, handleStop]);

  const mute = useCallback(() => {
    const { current: playerInstance } = playerInstanceRef;
    if (!playerInstance) {
      handlePlayerInstanceError();
      return;
    }

    playerInstance.setMute(true);
  }, [handlePlayerInstanceError]);

  const unmute = useCallback(() => {
    const { current: playerInstance } = playerInstanceRef;
    if (!playerInstance) {
      handlePlayerInstanceError();
      return;
    }

    if (playerInstance.getMute()) playerInstance.setMute(false);
  }, [handlePlayerInstanceError]);

  return [
    playerElementRef,
    {
      initialize,
      mute,
      pause,
      play,
      status,
      stop,
      unmute,
      urlLoaded,
    },
  ];
}
